"use client";

import { BotIcon, type LucideIcon, StickyNoteIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export type MemoVisualSource = {
  label: string;
  memoType?: "USER" | "SYSTEM" | null;
  sourceLabel?: string | null;
  sourceUrl?: string | null;
  links?: readonly { url: string; label?: string | null }[];
  systemLabels?: readonly {
    key?: string | null;
    name?: string | null;
    color?: string | null;
    localizedTexts?: readonly {
      locale?: string | null;
      name?: string | null;
    }[];
  }[];
};

export type MemoVisualIdentity =
  | {
      kind: "user-memo";
      label: "memo";
      Icon: LucideIcon;
    }
  | {
      kind: "system";
      label: "system";
      Icon: LucideIcon;
    }
  | { kind: "google-core-update"; label: "G"; Icon: null };

const CORE_UPDATE_LABEL_KEY = "core_update";
const DEFAULT_SYSTEM_MEMO_LABEL_COLOR = "#7c3aed";
const GOOGLE_CORE_UPDATE_MARKER_STROKE = "#94a3b8";
const GOOGLE_CORE_UPDATE_MARKER_TEXT = "#334155";

/**
 * 管理対象の Core Update label が付与された system memo だけを、固定の G mark 表示へ分類します。
 * label 名や URL は管理者が自由に編集できるため、視覚分類には使いません。
 */
export function isGoogleCoreUpdateMemo(memo: MemoVisualSource) {
  if (memo.memoType !== "SYSTEM") return false;
  return (
    memo.systemLabels?.some(
      (label) => label.key?.trim().toLowerCase() === CORE_UPDATE_LABEL_KEY,
    ) ?? false
  );
}

/**
 * system memo の先頭 label を、表示アイコンの基準 label として使います。
 */
export function primarySystemMemoLabel(memo: MemoVisualSource) {
  return memo.systemLabels?.find((label) => label.name || label.key);
}

/**
 * 管理画面と同じルールで、label 名から短い識別子を作ります。
 */
export function systemMemoLabelInitials(memo: MemoVisualSource) {
  if (isGoogleCoreUpdateMemo(memo)) return "G";

  const label = primarySystemMemoLabel(memo);
  const englishName = label?.localizedTexts?.find(
    (text) => text.locale === "en" && text.name?.trim(),
  )?.name;
  const labelName = (
    englishName ??
    label?.key ??
    label?.name ??
    memo.label
  ).trim();
  const words = labelName.split(/[\s_-]+/).filter(Boolean);
  const initials =
    words.length >= 2
      ? words
          .map((word) => word.at(0))
          .join("")
          .slice(0, 2)
      : (words[0] ?? labelName).slice(0, 2);
  return initials.toUpperCase() || "SA";
}

export function systemMemoLabelColor(memo: MemoVisualSource) {
  return primarySystemMemoLabel(memo)?.color ?? DEFAULT_SYSTEM_MEMO_LABEL_COLOR;
}

/** hex color は薄い背景にも使えるよう alpha を付けます。 */
export function hexColorWithAlpha(color: string | undefined, alpha: string) {
  return /^#[0-9a-f]{6}$/i.test(color ?? "") ? `${color}${alpha}` : undefined;
}

/**
 * chart marker / side panel / memos page で共通利用する system memo の視覚識別子です。
 * Core Update は従来どおり独立分類にし、表示自体は managed label 由来の短い文字で揃えます。
 */
export function memoVisualIdentity(memo: MemoVisualSource): MemoVisualIdentity {
  if (isGoogleCoreUpdateMemo(memo)) {
    return { kind: "google-core-update", label: "G", Icon: null };
  }
  if (memo.memoType === "SYSTEM") {
    return { kind: "system", label: "system", Icon: BotIcon };
  }
  return { kind: "user-memo", label: "memo", Icon: StickyNoteIcon };
}

/**
 * 複数 memo が同じ bucket に重なった時、chart marker に出す代表 identity を選びます。
 * Core Update は意味が強いため最優先、次に system memo、最後に通常 memo の順にします。
 */
export function representativeMemoVisualIdentity(
  memos: readonly MemoVisualSource[],
) {
  const googleCoreUpdate = memos.find(isGoogleCoreUpdateMemo);
  if (googleCoreUpdate) return memoVisualIdentity(googleCoreUpdate);
  const systemMemo = memos.find((memo) => memo.memoType === "SYSTEM");
  if (systemMemo) return memoVisualIdentity(systemMemo);
  return memoVisualIdentity(memos[0] ?? { label: "", memoType: "USER" });
}

/**
 * HTML 上の memo icon 表示です。system memo は管理画面と同じ label 由来の短い文字で表示します。
 */
export function MemoVisualIcon({
  memo,
  className,
  iconClassName,
}: {
  memo: MemoVisualSource;
  className?: string;
  iconClassName?: string;
}) {
  const identity = memoVisualIdentity(memo);
  if (identity.kind === "google-core-update" || identity.kind === "system") {
    const labelColor = systemMemoLabelColor(memo);
    const isGoogleCoreUpdate = identity.kind === "google-core-update";
    return (
      <span
        className={cn(
          "flex size-5 shrink-0 items-center justify-center rounded-sm border bg-background font-semibold text-[9px] leading-none",
          className,
        )}
        style={{
          borderColor: isGoogleCoreUpdate
            ? GOOGLE_CORE_UPDATE_MARKER_STROKE
            : labelColor,
          backgroundColor: isGoogleCoreUpdate
            ? "#ffffff"
            : hexColorWithAlpha(labelColor, "1A"),
          color: isGoogleCoreUpdate
            ? GOOGLE_CORE_UPDATE_MARKER_TEXT
            : labelColor,
        }}
        title={
          identity.kind === "google-core-update"
            ? "Google Core Update"
            : "System memo"
        }
      >
        <span
          className={cn(
            "flex size-full items-center justify-center whitespace-nowrap text-center leading-none",
            isGoogleCoreUpdate ? "text-[12px]" : "text-[9px]",
            iconClassName,
          )}
        >
          {systemMemoLabelInitials(memo)}
        </span>
      </span>
    );
  }

  const Icon = identity.Icon;
  return (
    <span
      className={cn(
        "flex size-5 shrink-0 items-center justify-center rounded-sm bg-amethyst-primary text-white",
        className,
      )}
      title="Memo"
    >
      <Icon className={cn("size-3", iconClassName)} />
    </span>
  );
}
